import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  LinearProgress,
  Avatar,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Analytics as AnalyticsIcon,
  TrendingUp as TrendingUpIcon,
  Person as PersonIcon,
  Assignment as AssignmentIcon,
  Schedule as ScheduleIcon,
  Star as StarIcon,
  Refresh as RefreshIcon,
  Download as DownloadIcon,
  AccessTime as AccessTimeIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Description as DescriptionIcon,
  AttachMoney as AttachMoneyIcon,
  ShowChart as ShowChartIcon,
  People as PeopleIcon,
  Build as BuildIcon,
  Notifications as NotificationsIcon,
  Event as EventIcon,
  Speed as SpeedIcon
} from '@mui/icons-material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Area,
  AreaChart
} from 'recharts';
import api from '../../config/axiosConfig';

const AnalyticaUsuarios = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [dateRange, setDateRange] = useState('30');
  const [selectedMetric, setSelectedMetric] = useState('activity');
  
  // Estados para métricas dinámicas
  const [userMetrics, setUserMetrics] = useState({
    quotationsCreated: 0,
    quotationsAccepted: 0,
    quotationsRejected: 0,
    totalValue: 0,
    acceptedValue: 0,
    conversionRate: 0,
    workOrdersAssigned: 0,
    workOrdersCompleted: 0,
    workOrdersPending: 0,
    notificationsPending: 0,
    eventsGenerated: 0,
    efficiencyGeneral: 0,
    avgResponseTime: 0,
    prospectsGenerated: 0
  });

  // Función para obtener métricas de usuario desde APIs
  const fetchUserMetrics = async () => {
    setLoading(true);
    try {
      // Obtener datos con manejo individual de errores
      let quotations = [];
      let workOrders = [];
      let notificationCount = 0;
      let events = [];
      let companies = [];

      // 1. Obtener cotizaciones
      try {
        const quotationsResponse = await api.get('/quotations');
        quotations = Array.isArray(quotationsResponse.data) ? quotationsResponse.data : [];
      } catch (err) {
        console.warn('Error al obtener cotizaciones:', err.message);
      }
      
      // 2. Obtener órdenes de trabajo
      try {
        const workOrdersResponse = await api.get('/work-orders');
        workOrders = Array.isArray(workOrdersResponse.data) ? workOrdersResponse.data : [];
      } catch (err) {
        console.warn('Error al obtener órdenes de trabajo:', err.message);
      }
      
      // 3. Obtener notificaciones pendientes
      try {
        const notificationsResponse = await api.get('/notifications/unread/count');
        notificationCount = typeof notificationsResponse.data === 'number' 
          ? notificationsResponse.data 
          : 0;
      } catch (err) {
        console.warn('Error al obtener notificaciones:', err.message);
      }
      
      // 4. Obtener eventos
      try {
        const eventsResponse = await api.get('/events/recent?limit=1000');
        events = Array.isArray(eventsResponse.data) ? eventsResponse.data : [];
      } catch (err) {
        console.warn('Error al obtener eventos:', err.message);
      }
      
      // 5. Obtener prospectos (empresas)
      try {
        const companiesResponse = await api.get('/companies');
        companies = Array.isArray(companiesResponse.data) ? companiesResponse.data : [];
      } catch (err) {
        console.warn('Error al obtener empresas:', err.message);
      }
      
      // Calcular métricas de cotizaciones
      const acceptedQuotations = quotations.filter(q => q.status === 'ACCEPTED');
      const rejectedQuotations = quotations.filter(q => q.status === 'REJECTED');
      const totalValue = quotations.reduce((sum, q) => sum + (parseFloat(q.totalAmount) || 0), 0);
      const acceptedValue = acceptedQuotations.reduce((sum, q) => sum + (parseFloat(q.totalAmount) || 0), 0);
      const conversionRate = quotations.length > 0 
        ? ((acceptedQuotations.length / quotations.length) * 100).toFixed(1)
        : 0;
      
      // Calcular métricas de órdenes de trabajo
      const completedOrders = workOrders.filter(wo => wo.status === 'COMPLETED');
      const pendingOrders = workOrders.filter(wo => 
        wo.status === 'PENDING' || wo.status === 'IN_PROGRESS'
      );
      
      // Calcular tiempo promedio de respuesta (en días)
      const quotationsWithDates = quotations.filter(q => 
        q.createdAt && q.statusUpdatedAt && q.status !== 'DRAFT'
      );
      const avgResponseTime = quotationsWithDates.length > 0
        ? quotationsWithDates.reduce((sum, q) => {
            const created = new Date(q.createdAt);
            const updated = new Date(q.statusUpdatedAt);
            const diffDays = (updated - created) / (1000 * 60 * 60 * 24);
            return sum + diffDays;
          }, 0) / quotationsWithDates.length
        : 0;
      
      // Calcular eficiencia general
      const completionRate = workOrders.length > 0
        ? (completedOrders.length / workOrders.length) * 100
        : 0;
      const efficiencyGeneral = Math.round(
        (parseFloat(conversionRate) * 0.6) + (completionRate * 0.4)
      );
      
      // Actualizar estado con métricas calculadas
      setUserMetrics({
        quotationsCreated: quotations.length,
        quotationsAccepted: acceptedQuotations.length,
        quotationsRejected: rejectedQuotations.length,
        totalValue: totalValue,
        acceptedValue: acceptedValue,
        conversionRate: parseFloat(conversionRate),
        workOrdersAssigned: workOrders.length,
        workOrdersCompleted: completedOrders.length,
        workOrdersPending: pendingOrders.length,
        notificationsPending: notificationCount,
        eventsGenerated: events.length,
        efficiencyGeneral: efficiencyGeneral,
        avgResponseTime: avgResponseTime.toFixed(1),
        prospectsGenerated: companies.length
      });
      
      // Verificar si al menos obtuvimos algunos datos
      const hasData = quotations.length > 0 || workOrders.length > 0 || companies.length > 0;
      
      if (hasData) {
        setError('');
      } else {
        setError('No se pudieron cargar las métricas. Verifica tu conexión o permisos.');
      }
    } catch (err) {
      console.error('Error crítico al obtener métricas:', err);
      setError(`Error al cargar las métricas: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };
  
  // useEffect para cargar métricas al montar el componente
  useEffect(() => {
    fetchUserMetrics();
  }, [dateRange]); // Recargar cuando cambie el rango de fechas

  // Función helper para formatear moneda
  const formatCurrency = (value) => {
    if (value >= 1000000) {
      return `$${(value / 1000000).toFixed(1)}M`;
    } else if (value >= 1000) {
      return `$${(value / 1000).toFixed(0)}K`;
    }
    return `$${value.toFixed(0)}`;
  };

  // Analytics data - ahora con datos reales del backend
  const [analyticsData, setAnalyticsData] = useState({
    totalUsers: 0,
    activeUsers: 0,
    inactiveUsers: 0,
    newUsersThisMonth: 0,
    totalQuotations: 0,
    
    userActivityData: [],
    userPerformanceData: [],
    roleDistribution: [],
    monthlyTrends: [],
    topPerformers: [],
    recentActivity: []
  });

  useEffect(() => {
    loadAnalytics();
  }, [dateRange]);

  const loadAnalytics = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Obtener estadísticas reales de usuarios desde el backend
      const [usersResponse, statsResponse, quotationsResponse, workOrdersResponse, eventsResponse] = await Promise.all([
        api.get('/users'),
        api.get('/users/role-statistics'),
        api.get('/quotations'),
        api.get('/work-orders').catch(() => ({ data: [] })),
        api.get('/events/recent?limit=10').catch(() => ({ data: [] }))
      ]);
      
      const users = Array.isArray(usersResponse.data) ? usersResponse.data : [];
      const stats = statsResponse.data;
      const quotations = Array.isArray(quotationsResponse.data) ? quotationsResponse.data : [];
      const workOrdersData = workOrdersResponse.data;
      const events = Array.isArray(eventsResponse.data) ? eventsResponse.data : [];
      
      // Manejar diferentes formatos de respuesta de work-orders
      let workOrders = [];
      if (Array.isArray(workOrdersData)) {
        workOrders = workOrdersData;
      } else if (workOrdersData && Array.isArray(workOrdersData.content)) {
        workOrders = workOrdersData.content;
      }
      
      console.log('📊 Datos cargados:', { users: users.length, quotations: quotations.length, workOrders: workOrders.length, events: events.length });
      
      // 1. DISTRIBUCIÓN POR ROLES (datos reales)
      const roleDistribution = calculateRoleDistribution(users);
      
      // 2. ACTIVIDAD SEMANAL (datos reales de cotizaciones)
      const userActivityData = calculateWeeklyActivity(quotations);
      
      // 3. TENDENCIAS MENSUALES (datos reales de cotizaciones)
      const monthlyTrends = calculateMonthlyTrends(quotations);
      
      // 4. TOP PERFORMERS (datos reales)
      const topPerformers = calculateTopPerformers(users, quotations);
      
      // 5. ACTIVIDAD RECIENTE (datos reales de eventos)
      const recentActivity = formatRecentActivity(events);
      
      // 6. RENDIMIENTO POR USUARIO (mejorado con work orders)
      const userPerformanceData = users.map(user => {
        const userQuotations = quotations.filter(q => q.createdBy === user.id);
        const acceptedQuotations = userQuotations.filter(q => q.status === 'ACCEPTED' || q.status === 'ACEPTADA');
        const userWorkOrders = workOrders.filter(wo => wo.assignedUserId === user.id);
        const completedOrders = userWorkOrders.filter(wo => wo.status === 'COMPLETED');
        
        const efficiency = userQuotations.length > 0 
          ? Math.round((acceptedQuotations.length / userQuotations.length) * 100)
          : 0;
        
        return {
          name: user.fullName || `${user.firstName || ''} ${user.firstLastName || ''}`.trim() || user.username || 'Usuario',
          quotations: userQuotations.length,
          workOrders: userWorkOrders.length,
          completedOrders: completedOrders.length,
          efficiency: efficiency
        };
      }).sort((a, b) => b.quotations - a.quotations);
      
      // Actualizar datos con información real
      setAnalyticsData({
        totalUsers: stats.totalUsuarios || users.length,
        activeUsers: stats.usuariosActivos || users.filter(u => u.isActive).length,
        inactiveUsers: stats.usuariosInactivos || users.filter(u => !u.isActive).length,
        newUsersThisMonth: calculateNewUsersThisMonth(users),
        totalQuotations: quotations.length,
        userActivityData,
        userPerformanceData,
        roleDistribution,
        monthlyTrends,
        topPerformers,
        recentActivity
      });
      
      setLoading(false);
    } catch (err) {
      console.error('Error al cargar analytics:', err);
      setError('Error al cargar los datos analíticos');
      setLoading(false);
    }
  };
  
  const calculateNewUsersThisMonth = (users) => {
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    
    return users.filter(user => {
      if (!user.createdAt) return false;
      const createdDate = new Date(user.createdAt);
      return createdDate.getMonth() === currentMonth && 
             createdDate.getFullYear() === currentYear;
    }).length;
  };
  
  // Calcular distribución por roles con datos reales
  const calculateRoleDistribution = (users) => {
    const roleColors = {
      'ADMIN': '#f44336',
      'ADMINISTRADOR': '#f44336',
      'USER': '#1976d2',
      'USUARIO': '#1976d2',
      'SUPERVISOR': '#ff9800',
      'INVITADO': '#9c27b0'
    };
    
    const roleCounts = {};
    users.forEach(user => {
      const roleName = user.role?.name || user.role || 'USUARIO';
      roleCounts[roleName] = (roleCounts[roleName] || 0) + 1;
    });
    
    return Object.entries(roleCounts).map(([name, value]) => ({
      name: name,
      value: value,
      color: roleColors[name] || '#9e9e9e'
    }));
  };
  
  // Calcular actividad semanal de cotizaciones
  const calculateWeeklyActivity = (quotations) => {
    const dayNames = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
    const dayCounts = [0, 0, 0, 0, 0, 0, 0];
    
    // Filtrar cotizaciones de los últimos 7 días
    const sevenDaysAgo = new Date();
    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
    
    quotations.forEach(q => {
      if (q.createdAt) {
        const date = new Date(q.createdAt);
        if (date >= sevenDaysAgo) {
          const dayIndex = date.getDay();
          dayCounts[dayIndex]++;
        }
      }
    });
    
    // Reorganizar para empezar en Lunes
    return [
      { name: 'Lun', quotations: dayCounts[1] },
      { name: 'Mar', quotations: dayCounts[2] },
      { name: 'Mié', quotations: dayCounts[3] },
      { name: 'Jue', quotations: dayCounts[4] },
      { name: 'Vie', quotations: dayCounts[5] },
      { name: 'Sáb', quotations: dayCounts[6] },
      { name: 'Dom', quotations: dayCounts[0] }
    ];
  };
  
  // Calcular tendencias mensuales de cotizaciones
  const calculateMonthlyTrends = (quotations) => {
    const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    const monthCounts = {};
    
    // Obtener últimos 6 meses
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${date.getFullYear()}-${date.getMonth()}`;
      monthCounts[key] = {
        month: monthNames[date.getMonth()],
        quotations: 0
      };
    }
    
    // Contar cotizaciones por mes
    quotations.forEach(q => {
      if (q.createdAt) {
        const date = new Date(q.createdAt);
        const key = `${date.getFullYear()}-${date.getMonth()}`;
        if (monthCounts[key]) {
          monthCounts[key].quotations++;
        }
      }
    });
    
    return Object.values(monthCounts);
  };
  
  // Calcular top performers
  const calculateTopPerformers = (users, quotations) => {
    const userStats = users.map(user => {
      const userQuotations = quotations.filter(q => q.createdBy === user.id);
      const acceptedQuotations = userQuotations.filter(q => q.status === 'ACCEPTED' || q.status === 'ACEPTADA');
      const efficiency = userQuotations.length > 0 
        ? Math.round((acceptedQuotations.length / userQuotations.length) * 100)
        : 0;
      
      const fullName = user.fullName || `${user.firstName || ''} ${user.firstLastName || ''}`.trim() || user.username || 'Usuario';
      const initials = fullName.split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2);
      
      return {
        name: fullName,
        avatar: initials,
        quotations: userQuotations.length,
        efficiency: efficiency
      };
    });
    
    // Ordenar por cotizaciones y tomar top 3
    return userStats
      .filter(u => u.quotations > 0)
      .sort((a, b) => b.quotations - a.quotations)
      .slice(0, 3);
  };
  
  // Formatear actividad reciente de eventos
  const formatRecentActivity = (events) => {
    return events.map(event => {
      const eventType = event.eventType || '';
      let type = 'default';
      
      if (eventType.includes('CREATED') || eventType.includes('ACCEPTED')) {
        type = 'success';
      } else if (eventType.includes('MODIFIED') || eventType.includes('UPDATED')) {
        type = 'warning';
      } else if (eventType.includes('REJECTED') || eventType.includes('DELETED')) {
        type = 'info';
      }
      
      return {
        user: event.createdBy || 'Sistema',
        action: event.description || 'Acción del sistema',
        time: formatTimeAgo(event.createdAt),
        type: type
      };
    });
  };
  
  const formatTimeAgo = (dateString) => {
    if (!dateString) return 'Reciente';
    const now = new Date();
    const eventDate = new Date(dateString);
    const diffInMinutes = Math.floor((now - eventDate) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Ahora';
    if (diffInMinutes < 60) return `${diffInMinutes} min`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)} hora${Math.floor(diffInMinutes / 60) > 1 ? 's' : ''}`;
    return `${Math.floor(diffInMinutes / 1440)} día${Math.floor(diffInMinutes / 1440) > 1 ? 's' : ''}`;
  };

  const exportReport = () => {
    // Simular exportación de reporte
    alert('Reporte exportado exitosamente');
  };

  const getActivityTypeIcon = (type) => {
    switch (type) {
      case 'success': return <CheckCircleIcon color="success" />;
      case 'warning': return <WarningIcon color="warning" />;
      case 'info': return <AssignmentIcon color="info" />;
      default: return <AccessTimeIcon color="action" />;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{
      width: '95%',
      maxWidth: '1400px',
      mx: 'auto',
      mt: 2,
      mb: 2,
      background: '#fff',
      borderRadius: 4,
      boxShadow: '0 4px 32px rgba(0,0,0,0.10)',
      p: 4,
    }}>
      {/* Header */}
      <Box sx={{ position: 'relative', mb: 4 }}>
        <Typography
          variant="h4"
          component="h1"
          sx={{
            fontWeight: 700,
            letterSpacing: 1,
            color: '#222',
            fontFamily: "'Fira Mono', 'Menlo', 'Monaco', 'Consolas', 'monospace'",
            textAlign: 'center',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            '&::before': {
              content: '"📊"',
              marginRight: 2,
              fontSize: '1.2em'
            }
          }}
        >
          Analítica de Usuarios
        </Typography>
        <Box sx={{ position: 'absolute', top: 0, right: 0, display: 'flex', gap: 1 }}>
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <InputLabel>Período</InputLabel>
            <Select
              value={dateRange}
              label="Período"
              onChange={(e) => setDateRange(e.target.value)}
            >
              <MenuItem value="7">7 días</MenuItem>
              <MenuItem value="30">30 días</MenuItem>
              <MenuItem value="90">90 días</MenuItem>
              <MenuItem value="365">1 año</MenuItem>
            </Select>
          </FormControl>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadAnalytics}
            sx={{ fontWeight: 600, textTransform: 'none' }}
          >
            Actualizar
          </Button>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={exportReport}
            sx={{ fontWeight: 600, textTransform: 'none' }}
          >
            Exportar
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* KPI Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Total Usuarios
                  </Typography>
                  <Typography variant="h4" component="div" color="primary">
                    {analyticsData.totalUsers}
                  </Typography>
                  <Typography variant="body2" color="success.main">
                    +{analyticsData.newUsersThisMonth} este mes
                  </Typography>
                </Box>
                <PersonIcon sx={{ fontSize: 40, color: '#1976d2' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Usuarios Activos
                  </Typography>
                  <Typography variant="h4" component="div" color="success.main">
                    {analyticsData.activeUsers}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {Math.round((analyticsData.activeUsers / analyticsData.totalUsers) * 100)}% del total
                  </Typography>
                </Box>
                <CheckCircleIcon sx={{ fontSize: 40, color: '#4caf50' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        
        
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography color="textSecondary" gutterBottom variant="body2">
                    Cotizaciones
                  </Typography>
                  <Typography variant="h4" component="div" color="warning.main">
                    {analyticsData.totalQuotations}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total del sistema
                  </Typography>
                </Box>
                <AssignmentIcon sx={{ fontSize: 40, color: '#ff9800' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={3}>
        {/* Activity Chart */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <Card sx={{ height: '400px' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <TrendingUpIcon sx={{ mr: 1 }} />
                Actividad Semanal de Usuarios
              </Typography>
              <ResponsiveContainer width="100%" height={320}>
                <BarChart data={analyticsData.userActivityData}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="name" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="quotations" fill="#1976d2" name="Cotizaciones" />
                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Role Distribution */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <Card sx={{ height: '400px' }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Distribución por Roles
              </Typography>
              <ResponsiveContainer width="100%" height={250}>
                <PieChart>
                  <Pie
                    data={analyticsData.roleDistribution}
                    cx="50%"
                    cy="50%"
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {analyticsData.roleDistribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <Box sx={{ mt: 2 }}>
                {analyticsData.roleDistribution.map((role, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        backgroundColor: role.color,
                        borderRadius: '50%',
                        mr: 1
                      }}
                    />
                    <Typography variant="body2">
                      {role.name}: {role.value} usuarios
                    </Typography>
                  </Box>
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Top Performers */}
        <Grid size={{ xs: 12, lg: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <StarIcon sx={{ mr: 1 }} />
                Usuarios Destacados
              </Typography>
              <List>
                {analyticsData.topPerformers.map((user, index) => (
                  <React.Fragment key={index}>
                    <ListItem>
                      <ListItemAvatar>
                        <Avatar sx={{ backgroundColor: '#1976d2' }}>
                          {user.avatar}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={user.name}
                        secondary={`${user.quotations} cotizaciones`}
                      />
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="body2" color="textSecondary">
                          {user.efficiency}% eficiencia
                        </Typography>
                      </Box>
                    </ListItem>
                    {index < analyticsData.topPerformers.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Recent Activity */}
        <Grid size={{ xs: 12, lg: 6 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <AccessTimeIcon sx={{ mr: 1 }} />
                Actividad Reciente
              </Typography>
              <List>
                {analyticsData.recentActivity.map((activity, index) => (
                  <React.Fragment key={index}>
                    <ListItem>
                      <ListItemAvatar>
                        {getActivityTypeIcon(activity.type)}
                      </ListItemAvatar>
                      <ListItemText
                        primary={activity.action}
                        secondary={`${activity.user} • ${activity.time}`}
                      />
                    </ListItem>
                    {index < analyticsData.recentActivity.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>

        {/* Detailed Performance Cards */}
        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h6">
                  📊 Rendimiento Detallado por Usuario
                </Typography>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<RefreshIcon />}
                  onClick={fetchUserMetrics}
                  disabled={loading}
                >
                  Actualizar
                </Button>
              </Box>
              
              {/* Error Alert */}
              {error && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}
              
              {/* Performance Metrics Cards */}
              <Grid container spacing={2} sx={{ mb: 4 }}>
                {/* Cotizaciones Creadas */}
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Card sx={{ 
                    background: 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)',
                    color: '#1565c0',
                    height: '100%',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <DescriptionIcon sx={{ fontSize: 32, mr: 1 }} />
                        <Typography variant="h4" fontWeight="bold">
                          {loading ? '...' : userMetrics.quotationsCreated}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Cotizaciones Creadas
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Servicios Autorizados */}
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Card sx={{ 
                    background: 'linear-gradient(135deg, #e8f5e9 0%, #c8e6c9 100%)',
                    color: '#2e7d32',
                    height: '100%',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <CheckCircleIcon sx={{ fontSize: 32, mr: 1 }} />
                        <Typography variant="h4" fontWeight="bold">
                          {loading ? '...' : userMetrics.quotationsAccepted}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Servicios Autorizados
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Valor Total Aceptado */}
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Card sx={{ 
                    background: 'linear-gradient(135deg, #fff3e0 0%, #ffe0b2 100%)',
                    color: '#e65100',
                    height: '100%',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <AttachMoneyIcon sx={{ fontSize: 32, mr: 1 }} />
                        <Typography variant="h4" fontWeight="bold">
                          {loading ? '...' : formatCurrency(userMetrics.acceptedValue)}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Valor Total Aceptado
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Tasa de Conversión */}
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Card sx={{ 
                    background: 'linear-gradient(135deg, #f3e5f5 0%, #e1bee7 100%)',
                    color: '#6a1b9a',
                    height: '100%',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <ShowChartIcon sx={{ fontSize: 32, mr: 1 }} />
                        <Typography variant="h4" fontWeight="bold">
                          {loading ? '...' : `${userMetrics.conversionRate}%`}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Tasa de Conversión
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Órdenes Asignadas */}
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Card sx={{ 
                    background: 'linear-gradient(135deg, #e0f2f1 0%, #b2dfdb 100%)',
                    color: '#00695c',
                    height: '100%',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <BuildIcon sx={{ fontSize: 32, mr: 1 }} />
                        <Typography variant="h4" fontWeight="bold">
                          {loading ? '...' : userMetrics.workOrdersAssigned}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        Órdenes Asignadas
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Órdenes Completadas */}
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Card sx={{ 
                    background: 'linear-gradient(135deg, #fce4ec 0%, #f8bbd0 100%)',
                    color: '#c2185b',
                    height: '100%',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <CheckCircleIcon sx={{ fontSize: 32, mr: 1, color: '#4caf50' }} />
                        <Typography variant="h4" fontWeight="bold">
                          {loading ? '...' : userMetrics.workOrdersCompleted}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        Órdenes Completadas
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Prospectos Generados */}
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Card sx={{ 
                    background: 'linear-gradient(135deg, #fff9c4 0%, #fff59d 100%)',
                    color: '#f57f17',
                    height: '100%',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <PeopleIcon sx={{ fontSize: 32, mr: 1 }} />
                        <Typography variant="h4" fontWeight="bold">
                          {loading ? '...' : userMetrics.prospectsGenerated}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        Prospectos Generados
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Eficiencia General */}
                <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                  <Card sx={{ 
                    background: 'linear-gradient(135deg, #e8eaf6 0%, #c5cae9 100%)',
                    color: '#3949ab',
                    height: '100%',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <SpeedIcon sx={{ fontSize: 32, mr: 1, color: '#2196f3' }} />
                        <Typography variant="h4" fontWeight="bold">
                          {loading ? '...' : `${userMetrics.efficiencyGeneral}%`}
                        </Typography>
                      </Box>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        Eficiencia General
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Divider sx={{ my: 3 }} />

              <Typography variant="h6" gutterBottom sx={{ mt: 3 }}>
                Tabla Detallada de Rendimiento
              </Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Usuario</TableCell>
                      <TableCell align="center">Cotizaciones</TableCell>
                      <TableCell align="center">Órdenes Trabajo</TableCell>
                      <TableCell align="center">Completadas</TableCell>
                      <TableCell align="center">Eficiencia</TableCell>
                      <TableCell align="center">Estado</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analyticsData.userPerformanceData.map((user, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Avatar sx={{ mr: 2, width: 32, height: 32 }}>
                              {user.name.split(' ').map(n => n[0]).join('')}
                            </Avatar>
                            {user.name}
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight="bold" color="primary">
                            {user.quotations}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight="bold" color="info.main">
                            {user.workOrders || 0}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Typography variant="body2" fontWeight="bold" color="success.main">
                            {user.completedOrders || 0}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Box sx={{ width: '100%', mr: 1 }}>
                              <LinearProgress
                                variant="determinate"
                                value={user.efficiency}
                                sx={{ height: 8, borderRadius: 4 }}
                              />
                            </Box>
                            <Typography variant="body2" color="textSecondary">
                              {user.efficiency}%
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={user.efficiency >= 85 ? 'Excelente' : user.efficiency >= 70 ? 'Bueno' : 'Regular'}
                            color={user.efficiency >= 85 ? 'success' : user.efficiency >= 70 ? 'primary' : 'warning'}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Monthly Trends */}
        <Grid size={{ xs: 12 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Tendencias Mensuales
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={analyticsData.monthlyTrends}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="month" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Area type="monotone" dataKey="quotations" stroke="#1976d2" fill="#1976d2" name="Cotizaciones" />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default AnalyticaUsuarios;
